// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  ContaPagarDrawer.tsx
//  Sheet lateral com detalhe completo de uma conta a pagar + histórico baixas.
// ════════════════════════════════════════════════════════════════════════════

import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { useDetalheContaPagar } from '../hooks/useDetalheContaPagar';
import { calcularStatus } from '../types';

interface ContaPagarDrawerProps {
  id: number | null;
  onClose: () => void;
}

const brl = (v: number | null | undefined) =>
  (v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const fmt = (d: string | null | undefined) =>
  d ? new Date(d).toLocaleDateString('pt-BR') : '—';

export function ContaPagarDrawer({ id, onClose }: ContaPagarDrawerProps) {
  const { data: conta, isLoading } = useDetalheContaPagar(id);

  const status = conta ? calcularStatus(conta.isPago, conta.dtVencimento) : null;

  // Uma linha de baixa só é um PAGAMENTO se estiver quitada. As demais são parcelas
  // ainda em aberto e vão para a sua própria lista — misturar as duas era o que fazia
  // uma parcela futura aparecer como baixa com data 31/12/1899 (data vazia do legado).
  const baixasPagas     = (conta?.baixas ?? []).filter((b) => b.isPago);
  const parcelasAbertas = (conta?.baixas ?? [])
    .filter((b) => !b.isPago)
    .sort((a, b) => a.nroParcela - b.nroParcela);

  const statusColor: Record<string, string> = {
    pago:     'bg-green-500/10 text-green-600',
    pendente: 'bg-yellow-500/10 text-yellow-600',
    atrasado: 'bg-red-500/10 text-red-600',
    recebido: 'bg-green-500/10 text-green-600',
  };

  return (
    <Sheet open={id !== null} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader className="mb-4">
          <SheetTitle>Detalhe — Conta a Pagar #{id}</SheetTitle>
        </SheetHeader>

        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-5 w-full" />)}
          </div>
        ) : !conta ? (
          <p className="text-sm text-muted-foreground">Conta não encontrada.</p>
        ) : (
          <div className="space-y-6">
            {/* ── Cabeçalho ── */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Documento</p>
                <p className="font-semibold">{conta.numDocumento || conta.duplicata || `#${conta.id}`}</p>
              </div>
              {status && (
                <Badge className={statusColor[status]}>
                  {status === 'pago' ? <CheckCircle className="h-3 w-3 mr-1" />
                    : status === 'atrasado' ? <AlertCircle className="h-3 w-3 mr-1" />
                    : <Clock className="h-3 w-3 mr-1" />}
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </Badge>
              )}
            </div>

            {/* ── Dados principais ── */}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-muted-foreground text-xs">Fornecedor</p>
                <p title={`Fornecedor #${conta.fornecedorId}`}>
                  {conta.fornecedorNome || `Forn. #${conta.fornecedorId}`}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Parcela</p>
                <p>{conta.nroParcela}/{conta.qtdParcelas}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Emissão</p>
                <p>{fmt(conta.dtEmissao)}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Vencimento</p>
                <p>{fmt(conta.dtVencimento)}</p>
              </div>
              {conta.dtPagamento && (
                <div>
                  <p className="text-muted-foreground text-xs">Pagamento</p>
                  <p>{fmt(conta.dtPagamento)}</p>
                </div>
              )}
              {conta.contrato && (
                <div>
                  <p className="text-muted-foreground text-xs">Contrato</p>
                  <p>{conta.contrato}</p>
                </div>
              )}
            </div>

            <Separator />

            {/* ── Valores ── */}
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Valor Original</span>
                <span>{brl(conta.valorOriginal)}</span>
              </div>
              {conta.valorDesconto > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Desconto</span>
                  <span>– {brl(conta.valorDesconto)}</span>
                </div>
              )}
              {conta.valorAcrescimo > 0 && (
                <div className="flex justify-between text-red-600">
                  <span>Acréscimo</span>
                  <span>+ {brl(conta.valorAcrescimo)}</span>
                </div>
              )}
              <Separator />
              <div className="flex justify-between font-semibold">
                <span>Valor Total</span>
                <span>{brl(conta.valorTotal)}</span>
              </div>
              <div className="flex justify-between font-semibold text-orange-600">
                <span>Saldo Devedor</span>
                <span>{brl(conta.valorSaldo)}</span>
              </div>
              {conta.isPago && (
                <div className="flex justify-between font-semibold text-green-600">
                  <span>Valor Pago</span>
                  <span>{brl(conta.valorPago)}</span>
                </div>
              )}
            </div>

            {/* ── Baixas realizadas x parcelas ainda em aberto ──
                Antes as duas coisas iam juntas em "Histórico de Baixas": uma parcela
                em aberto aparecia como se fosse um pagamento, com data vazia do legado
                (31/12/1899) e valor pago R$ 0,00. São listas distintas. */}
            {baixasPagas.length > 0 && (
              <>
                <Separator />
                <div>
                  <h3 className="text-sm font-semibold mb-2">
                    Histórico de Baixas ({baixasPagas.length})
                  </h3>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Dt. Pgto.</TableHead>
                        <TableHead className="text-right">Valor Pago</TableHead>
                        <TableHead className="text-right">Desconto</TableHead>
                        <TableHead className="text-right">Saldo</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {baixasPagas.map((b) => (
                        <TableRow key={b.id}>
                          <TableCell>{fmt(b.dtPagamento)}</TableCell>
                          <TableCell className="text-right text-green-600">{brl(b.valorPago)}</TableCell>
                          <TableCell className="text-right">{brl(b.valorDesconto)}</TableCell>
                          <TableCell className="text-right">{brl(b.saldo)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}

            {parcelasAbertas.length > 0 && (
              <>
                <Separator />
                <div>
                  <h3 className="text-sm font-semibold mb-2">
                    Parcelas em Aberto ({parcelasAbertas.length})
                  </h3>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Parcela</TableHead>
                        <TableHead>Vencimento</TableHead>
                        <TableHead className="text-right">Valor</TableHead>
                        <TableHead className="text-right">Saldo</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {parcelasAbertas.map((b) => (
                        <TableRow key={b.id}>
                          <TableCell>{b.nroParcela}</TableCell>
                          <TableCell>{fmt(b.dtVencimento)}</TableCell>
                          <TableCell className="text-right">{brl(b.valorTotal)}</TableCell>
                          <TableCell className="text-right font-medium">{brl(b.saldo)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
